import { useEffect } from "react";
import { useSessions, useCreateSession } from "./useSessions";
import type { SessionResponseDto } from "@tabipla/shared";

interface UseDefaultSessionResult {
  session: SessionResponseDto | undefined;
  isLoading: boolean;
  isCreating: boolean;
}

/**
 * Hook that automatically manages a default session for a group.
 * - Returns the first active session if one exists
 * - Creates a new session automatically if no active session exists
 * - Hides the complexity of session management from the UI
 */
export function useDefaultSession(
  groupId: string | undefined,
  canEdit: boolean
): UseDefaultSessionResult {
  const { data: sessions, isLoading: sessionsLoading } = useSessions(groupId);
  const createSessionMutation = useCreateSession(groupId ?? "");

  // Find a non-completed session or fallback to the most recent one
  const activeSession = sessions?.find((s) => s.status !== "completed");
  const latestSession = sessions?.[0];
  const defaultSession = activeSession ?? latestSession;

  // Auto-create a session if none exists and user can edit
  useEffect(() => {
    if (
      groupId &&
      canEdit &&
      !sessionsLoading &&
      sessions &&
      sessions.length === 0 &&
      !createSessionMutation.isPending
    ) {
      // Create a default session
      const now = new Date();
      const monthName = now.toLocaleDateString("ja-JP", { month: "long" });
      createSessionMutation.mutate({
        name: `${monthName}の経費`,
      });
    }
  }, [groupId, canEdit, sessionsLoading, sessions, createSessionMutation]);

  return {
    session: defaultSession,
    isLoading: sessionsLoading,
    isCreating: createSessionMutation.isPending,
  };
}
